import dayjs from "dayjs"
import { Button as AntButton, Checkbox, DatePicker, Input, Select, Space } from "antd"
import { InfoCircleFilled } from "@ant-design/icons"
import { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { Button as BootstrapButton, Col, Form, Modal, Row } from "react-bootstrap"
import Autocomplete from "../Autocomplete"
import "../BorrowerApplication.css"
import { AddressComponent } from "../Common/AddressAutocomplete"
import CheckGroup from "../Common/components/CheckGroup"
import { DollarInputV2, moneyTypingValue } from "./DollarInputV2"
import { Tips } from "../Common/Tooltip"
import * as com from "../Common"
import { titles } from "../occupations"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const EMPLOYMENT_MODAL_FIELD_ORDER = [
    "employername",
    "positiontitle",
    "phone",
    "from",
    "lineofwork",
    "base",
    "incomeorloss",
    "selfincomedoc",
]

const PREVIOUS_EMPLOYMENT_MODAL_FIELD_ORDER = [
    "employername",
    "positiontitle",
    "from",
    "to",
]

const isPhoneFilled = (value) => value && value !== com.phonePlaceholder

const validateEmploymentDraft = (draft) => {
    const errors = {}
    if (!draft.employername) {
        errors.employername = "Please enter your employer."
    }
    if (!draft.positiontitle) {
        errors.positiontitle = "Please enter your position/title."
    }
    if (!isPhoneFilled(draft.phone)) {
        errors.phone = "Please enter your phone number."
    } else if (draft.phone.length !== 14) {
        errors.phone = com.phoneRegexMessage
    }
    if (!draft.from) {
        errors.from = "Please provide your start date."
    }
    if (`${draft.yearsatlineofwork ?? ""}`.trim() === "" && `${draft.monthsatlineofwork ?? ""}`.trim() === "") {
        errors.lineofwork = "Please tell us how long you have worked in this industry."
    }
    if (!draft.selfemployed && !(parseFloat(draft.income?.base) > 0)) {
        errors.base = "Please provide your base income."
    }
    if (draft.selfemployed && draft.incomeorloss === "") {
        errors.incomeorloss = "Please provide your income or loss amount."
    }
    if (draft.selfemployed && !draft.selfincomedoc) {
        errors.selfincomedoc = "Please select a documentation option."
    }
    const firstErrorKey = EMPLOYMENT_MODAL_FIELD_ORDER.find((key) => !!errors[key]) || null
    return { errors, firstErrorKey }
}

const validatePreviousEmploymentDraft = (draft) => {
    const errors = {}
    if (!draft.employername) {
        errors.employername = "Please enter your employer."
    }
    if (!draft.positiontitle) {
        errors.positiontitle = "Please enter your position/title."
    }
    if (!draft.from) {
        errors.from = "Please provide your start date."
    }
    if (!draft.to) {
        errors.to = "Please provide your end date."
    }
    const firstErrorKey = PREVIOUS_EMPLOYMENT_MODAL_FIELD_ORDER.find((key) => !!errors[key]) || null
    return { errors, firstErrorKey }
}

const emptyIncome = {
    base: "",
    basemonthly: "monthly",
    basehoursperweek: "",
    overtime: "",
    overtimemonthly: "monthly",
    bonuses: "",
    bonusesmonthly: "monthly",
    commissions: "",
    commissionsmonthly: "monthly",
    military: "",
    militarymonthly: "monthly",
    other: "",
    othermonthly: "monthly",
}

const emptyOccupation = {
    key: "",
    hasoccupation: "",
    employername: "",
    employeraddress: "",
    employerstreet: "",
    employerunit: "",
    employercity: "",
    employercounty: "",
    employerstate: "",
    employerzip: "",
    from: "",
    positiontitle: "",
    typeofbusiness: "",
    phone: "",
    yearsatlineofwork: "",
    monthsatlineofwork: "",
    selfemployed: false,
    employedbyagent: false,
    percentownership: "",
    incomeorloss: "",
    incomeorlossperiod: "monthly",
    income: emptyIncome,
    selfincomedoc: "",
}

const emptyPreviousOccupation = {
    key: "",
    employername: "",
    employeraddress: "",
    employerstreet: "",
    employerunit: "",
    employercity: "",
    employercounty: "",
    employerstate: "",
    employerzip: "",
    from: "",
    to: "",
    positiontitle: "",
    typeofbusiness: "",
    phone: "",
    income: "",
    incomeperiod: "yearly",
    selfemployed: false,
    employedbyagent: false,
}

const ensureKey = (item, prefix, index) => item.key || `${prefix}-${Date.now()}-${index}`

const asString = (value) => (value == null || value === "" ? "" : `${value}`)

const addressParser = require("parse-address")

const parseEmployerAddress = (address) => {
    const parsed = addressParser.parseLocation(address || "") || {}
    const street = [parsed.number, parsed.prefix, parsed.street, parsed.type, parsed.suffix].filter(Boolean).join(" ")
    return {
        employerstreet: street || address || "",
        employerunit: parsed.sec_unit_num ? `${parsed.sec_unit || ""} ${parsed.sec_unit_num}`.trim() : "",
        employercity: parsed.city || "",
        employerstate: parsed.state || "",
        employerzip: parsed.zip || "",
    }
}

const normalizeEmployerAddress = (occupation = {}) => {
    const parsed = parseEmployerAddress(occupation.employeraddress)
    const parsedState = parsed.employerstate.length === 2
        ? com.capitalize(com.convertAbbStateToFull(parsed.employerstate))
        : parsed.employerstate
    return {
        employerstreet: occupation.employerstreet || parsed.employerstreet,
        employerunit: occupation.employerunit || parsed.employerunit,
        employercity: occupation.employercity || parsed.employercity,
        employercounty: occupation.employercounty || "",
        employerstate: occupation.employerstate || parsedState,
        employerzip: occupation.employerzip || parsed.employerzip,
    }
}

const formatEmployerAddress = (address) => [
    [address.employerstreet, address.employerunit].filter(Boolean).join(", "),
    address.employercity,
    address.employerstate,
    address.employerzip,
].filter(Boolean).join(", ")

const MONEY_INCOME_FIELDS = ["base", "basehoursperweek", "overtime", "bonuses", "commissions", "military", "other"]
const EMPLOYMENT_ADDRESS_PATTERN = "^[0-9]+\\s[\\p{L}\\p{N} !:,&()'?#+\\/@-]+$"

const normalizeIncome = (income = {}) => {
    const next = {
        ...emptyIncome,
        ...income,
        basemonthly: income.basemonthly || "monthly",
        overtimemonthly: income.overtimemonthly || "monthly",
        bonusesmonthly: income.bonusesmonthly || "monthly",
        commissionsmonthly: income.commissionsmonthly || "monthly",
        militarymonthly: income.militarymonthly || "monthly",
        othermonthly: income.othermonthly || "monthly",
    }
    MONEY_INCOME_FIELDS.forEach((field) => {
        next[field] = asString(next[field])
    })
    return next
}

const normalizeOccupation = (occupation = {}) => ({
    ...emptyOccupation,
    ...occupation,
    ...normalizeEmployerAddress(occupation),
    income: normalizeIncome(occupation.income),
    incomeorloss: asString(occupation.incomeorloss),
    percentownership: asString(occupation.percentownership),
    yearsatlineofwork: asString(occupation.yearsatlineofwork),
    monthsatlineofwork: asString(occupation.monthsatlineofwork),
    incomeorlossperiod: occupation.incomeorlossperiod || "monthly",
})

const normalizePreviousOccupation = (occupation = {}) => ({
    ...emptyPreviousOccupation,
    ...occupation,
    ...normalizeEmployerAddress(occupation),
    income: asString(occupation.income),
    incomeperiod: occupation.incomeperiod || "yearly",
})

const serializeOccupation = (occupation, previous = false) => {
    const normalized = previous ? normalizePreviousOccupation(occupation) : normalizeOccupation(occupation)
    return {
        ...normalized,
        employeraddress: formatEmployerAddress(normalized) || normalized.employeraddress || "",
    }
}

const stringifyEmploymentPayload = ({ occupation, otheroccupations, previousoccupations, nonqm }) => ({
    occupation: serializeOccupation(occupation),
    otheroccupations: (otheroccupations || []).map((item) => serializeOccupation(item)),
    previousoccupations: (previousoccupations || []).map((item) => serializeOccupation(item, true)),
    ...(nonqm ? {
        nonqm: {
            ...nonqm,
            nonqmincomeamount: asString(nonqm.nonqmincomeamount),
            nonqmincomeperiod: nonqm.nonqmincomeperiod || "monthly",
            nonqmincometype: nonqm.nonqmincometype || [],
        },
    } : {}),
})

const formatAmount = (value) => (
    value ? Number(value).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }) : "--"
)

const formatTaxes = (value) => ({
    schedulec: "Schedule C",
    schedulef: "Schedule F",
    f1065: "1065 Partnership Return",
    f1120S: "1120S for S Corp",
    f1120: "1120 for C Corp",
    w2paystub: "W2/Paystubs",
    schedulek1: "Schedule K1",
}[value] || "--")

const calculateMonthly = (amount, period, hoursPerWeek = null) => {
    const value = parseFloat(amount)
    if (Number.isNaN(value)) return 0
    if (period === "yearly") return value / 12
    if (period === "hourly") {
        const hours = parseFloat(hoursPerWeek)
        return Number.isNaN(hours) ? 0 : (value * hours * 52) / 12
    }
    return value
}

const calculateEmploymentMonthlyIncome = (occupation) => {
    if (occupation.selfemployed) {
        return calculateMonthly(occupation.incomeorloss, occupation.incomeorlossperiod)
    }
    return ["base", "commissions", "overtime", "bonuses", "military", "other"].reduce((sum, field) => (
        sum + calculateMonthly(
            occupation.income[field],
            occupation.income[`${field}monthly`],
            field === "base" ? occupation.income.basehoursperweek : null
        )
    ), 0)
}

const incomeRows = (occupation) => ([
    { label: "Employer", value: occupation.employername || "--", show: true },
    { label: "Position/Title", value: occupation.positiontitle || "--", show: true },
    { label: "Address", value: com.compactAddressDisplay(occupation.employeraddress) || "--", span: 12, show: true },
    { label: "Date", value: occupation.from ? `${occupation.from} - Present` : "--", span: 12, show: true },
    { label: "Base", value: formatAmount(occupation.income.base), show: !occupation.selfemployed, monthly: occupation.income.basemonthly },
    { label: "Commission", value: occupation.income.commissions ? formatAmount(occupation.income.commissions) : "--", show: !occupation.selfemployed, monthly: occupation.income.commissionsmonthly },
    { label: "Overtime", value: occupation.income.overtime ? formatAmount(occupation.income.overtime) : "--", show: !occupation.selfemployed, monthly: occupation.income.overtimemonthly },
    { label: "Bonus", value: occupation.income.bonuses ? formatAmount(occupation.income.bonuses) : "--", show: !occupation.selfemployed, monthly: occupation.income.bonusesmonthly },
    { label: "Military", value: occupation.income.military ? formatAmount(occupation.income.military) : "--", show: !occupation.selfemployed, monthly: occupation.income.militarymonthly },
    { label: "Other", value: occupation.income.other ? formatAmount(occupation.income.other) : "--", show: !occupation.selfemployed, monthly: occupation.income.othermonthly },
    { label: "Ownership share", value: occupation.percentownership !== "" ? `${occupation.percentownership}%` : "--", show: occupation.selfemployed },
    {
        label: "Income or Loss",
        value: occupation.incomeorloss ? `${formatAmount(occupation.incomeorloss)}(${occupation.incomeorlossperiod})` : "--",
        show: occupation.selfemployed,
    },
    {
        label: parseInt(occupation.percentownership || "0", 10) >= 25 ? "Taxes" : "Document income",
        value: formatTaxes(occupation.selfincomedoc),
        show: occupation.selfemployed,
    },
])

const previousRows = (occupation) => ([
    { label: "Employer", value: occupation.employername || "--" },
    { label: "Position/Title", value: occupation.positiontitle || "--" },
    { label: "Address", value: com.compactAddressDisplay(occupation.employeraddress) || "--", span: 12 },
    { label: "Dates", value: `${occupation.from || "--"} - ${occupation.to || "--"}` },
    { label: "Total income", value: `${formatAmount(occupation.income)}/${occupation.incomeperiod || "yearly"}` },
])

const IncomeType = ({ label, field, income, onIncomeChange, error }) => (
    <Col md={6} xs={12} className="d-block mt-4 p-0" data-field={field === "base" ? "base" : undefined}>
        <Form.Group controlId={`amount${field}`} className="text-left" style={{ width: "100%" }}>
            <Form.Label className="text-left text-nowrap employment-label">{field === "base" ? "*" : ""}{label}</Form.Label>
            <Space.Compact style={{ width: "96%" }}>
                <DollarInputV2
                    className="income-dollar-input w-100"
                    value={income[field]}
                    onChange={(value) => onIncomeChange(field, value)}
                />
                <Select
                    style={{ height: 44 }}
                    value={income[`${field}monthly`]}
                    onChange={(value) => onIncomeChange(`${field}monthly`, value)}
                >
                    <Select.Option value="monthly">Monthly</Select.Option>
                    <Select.Option value="yearly">Yearly</Select.Option>
                </Select>
            </Space.Compact>
            <FieldError message={error} />
        </Form.Group>
    </Col>
)

const EmploymentFields = ({ draft, setDraft, fieldErrors = {}, clearFieldError }) => {
    const setField = (field, value) => {
        setDraft((prev) => ({ ...prev, [field]: value == null ? "" : value }))
        if (clearFieldError && (field === "incomeorloss" || field === "employername" || field === "phone" || field === "selfincomedoc" || field === "from")) {
            clearFieldError(field)
        }
    }
    const setAddressField = (field, value) => {
        setDraft((prev) => {
            const next = { ...prev, [field]: value == null ? "" : `${value}` }
            return { ...next, employeraddress: formatEmployerAddress(next) }
        })
    }
    const setIncomeField = (field, value) => {
        setDraft((prev) => ({ ...prev, income: { ...prev.income, [field]: value == null ? "" : `${value}` } }))
        if (field === "base" && clearFieldError) clearFieldError("base")
    }
    // Time at this employer, as years and months: the starting suggestion for
    // "how long in this industry" (it can only be longer).
    const monthsSince = (from) => {
        const date = from ? dayjs(from, "MM/DD/YYYY") : null
        return date && date.isValid() ? Math.max(dayjs().diff(date, "month"), 0) : null
    }
    const handleStartDate = (date) => {
        if (clearFieldError) clearFieldError("from")
        setDraft((prev) => {
            // The industry answer follows the start date until the borrower
            // has typed their own.
            const before = monthsSince(prev.from)
            const suggestedBefore = before === null ? { y: "", m: "" } : { y: `${Math.floor(before / 12)}`, m: `${before % 12}` }
            const untouched =
                (prev.yearsatlineofwork === "" && prev.monthsatlineofwork === "") ||
                (prev.yearsatlineofwork === suggestedBefore.y && prev.monthsatlineofwork === suggestedBefore.m)
            if (!date) {
                return { ...prev, from: "", ...(untouched ? { yearsatlineofwork: "", monthsatlineofwork: "" } : {}) }
            }
            const totalMonths = Math.max(dayjs().diff(date, "month"), 0)
            return {
                ...prev,
                from: date.format("MM/DD/YYYY"),
                ...(untouched
                    ? { yearsatlineofwork: `${Math.floor(totalMonths / 12)}`, monthsatlineofwork: `${totalMonths % 12}` }
                    : {}),
            }
        })
    }
    const setLineOfWork = (field, raw) => {
        const digits = `${raw ?? ""}`.replace(/\D/g, "").slice(0, 2)
        const value = field === "monthsatlineofwork" && parseInt(digits, 10) > 11 ? "11" : digits
        setDraft((prev) => ({ ...prev, [field]: value }))
        if (clearFieldError) clearFieldError("lineofwork")
    }

    return (
        <>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12} data-field="employername">
                    <Form.Group controlId="CEmployer1" className="text-left input-groups">
                        <Form.Label className="text-left text-nowrap employment-label">*Employer</Form.Label>
                        <Form.Control
                            placeholder="Enter your employer"
                            type="text"
                            value={draft.employername}
                            isInvalid={!!fieldErrors.employername}
                            onChange={(event) => setField("employername", event.target.value)}
                        />
                        <FieldError message={fieldErrors.employername} />
                    </Form.Group>
                </Col>
                <Col className="ml-0 pl-0 mt-4 employment-autocomplete" md={6} xs={12} data-field="positiontitle">
                    <Autocomplete
                        id="cOtherTitle"
                        suggestions={titles}
                        name="position"
                        type="text"
                        label="*Position/Title"
                        value={draft.positiontitle}
                        onChange={(event) => {
                            setDraft((prev) => ({ ...prev, positiontitle: event.target.value == null ? "" : event.target.value }))
                            if (clearFieldError) clearFieldError("positiontitle")
                        }}
                    />
                    <FieldError message={fieldErrors.positiontitle} />
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12} data-field="phone">
                    <Form.Group className="text-left">
                        <Form.Label className="text-left text-nowrap">
                            *Phone number
                            <Tips content={<div>Specify the HR/Personnel or hiring manager phone number to verify employment.</div>} />
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={com.phonePlaceholder}
                            value={draft.phone}
                            isInvalid={!!fieldErrors.phone}
                            onChange={(event) => setField("phone", com.fixPhoneInput(event.target.value))}
                        />
                        <FieldError message={fieldErrors.phone} />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={12} xs={12}>
                    <AddressComponent
                        id="EmployerAddr"
                        label="Employment address"
                        address={draft.employerstreet}
                        unit={draft.employerunit}
                        state={draft.employerstate}
                        county={draft.employercounty}
                        city={draft.employercity}
                        zip={draft.employerzip}
                        onAddressLineChange={(value) => setAddressField("employerstreet", value)}
                        onUnitChange={(value) => setAddressField("employerunit", value)}
                        onStateChange={(value) => setAddressField("employerstate", value)}
                        onCountyChange={(value) => setAddressField("employercounty", value)}
                        onCityChange={(value) => setAddressField("employercity", value)}
                        onZipChange={(value) => setAddressField("employerzip", value)}
                        pattern={EMPLOYMENT_ADDRESS_PATTERN}
                        validateZip
                        placeholder="Street address"
                    />
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12} data-field="from">
                    <Form.Group controlId="from" className="text-left">
                        <Form.Label className="text-left text-nowrap">*Start date</Form.Label>
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="MM/DD/YYYY"
                            format="MM/DD/YYYY"
                            status={fieldErrors.from ? "error" : undefined}
                            value={draft.from ? dayjs(draft.from, "MM/DD/YYYY") : null}
                            onChange={handleStartDate}
                        />
                        <FieldError message={fieldErrors.from} />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={12} xs={12} data-field="lineofwork">
                    <Form.Group className="text-left">
                        <Form.Label className="text-left">*How long have you been working in this industry?</Form.Label>
                        <div className="employment-v2-duration">
                            <Input
                                size="large"
                                inputMode="numeric"
                                suffix="years"
                                aria-label="Years working in this industry"
                                status={fieldErrors.lineofwork ? "error" : undefined}
                                value={draft.yearsatlineofwork}
                                onChange={(event) => setLineOfWork("yearsatlineofwork", event.target.value)}
                            />
                            <Input
                                size="large"
                                inputMode="numeric"
                                suffix="months"
                                aria-label="Months working in this industry"
                                status={fieldErrors.lineofwork ? "error" : undefined}
                                value={draft.monthsatlineofwork}
                                onChange={(event) => setLineOfWork("monthsatlineofwork", event.target.value)}
                            />
                        </div>
                        <FieldError message={fieldErrors.lineofwork} />
                    </Form.Group>
                </Col>
            </Row>
            {!draft.selfemployed && (
                <>
                    <Row>
                        <IncomeType label="Base" field="base" income={draft.income} onIncomeChange={setIncomeField} error={fieldErrors.base} />
                        <IncomeType label="Commissions" field="commissions" income={draft.income} onIncomeChange={setIncomeField} />
                    </Row>
                    <Row><IncomeType label="Overtime" field="overtime" income={draft.income} onIncomeChange={setIncomeField} /><IncomeType label="Bonuses" field="bonuses" income={draft.income} onIncomeChange={setIncomeField} /></Row>
                    <Row><IncomeType label="Military" field="military" income={draft.income} onIncomeChange={setIncomeField} /><IncomeType label="Other" field="other" income={draft.income} onIncomeChange={setIncomeField} /></Row>
                </>
            )}
            {draft.selfemployed && (
                <>
                    <Row>
                        <Col className="ml-0 pl-0 mt-4" md={4} xs={12}>
                            <Form.Group controlId="share" className="text-left">
                                <Form.Label className="text-left text-nowrap">Ownership share</Form.Label>
                                <Input suffix="%" value={draft.percentownership} onChange={(event) => {
                                    const value = event.target.value.replaceAll(".", "")
                                    setField("percentownership", parseInt(value || "0", 10) > 100 ? "100" : value)
                                }} />
                            </Form.Group>
                        </Col>
                        <Col className="ml-0 pl-0 mt-4" md={4} xs={12}>
                            <Form.Group controlId="incomeorlossperiod" className="text-left">
                                <Form.Label className="text-left text-nowrap">*Income or Loss</Form.Label>
                                <Select style={{ width: "100%", height: 44 }} value={draft.incomeorlossperiod} onChange={(value) => setField("incomeorlossperiod", value)}>
                                    <Select.Option value="monthly">Monthly</Select.Option>
                                    <Select.Option value="yearly">Yearly</Select.Option>
                                </Select>
                            </Form.Group>
                        </Col>
                        <Col className="ml-0 pl-0 mt-4" md={4} xs={12} data-field="incomeorloss">
                            <Form.Group controlId="incomeorloss" className="text-left">
                                <Form.Label className="text-left text-nowrap">*Amount</Form.Label>
                                <div className="InputWithSignWrap"><div className="InputWithSign-prefix">$</div><Input className="InputWithSign-input" value={draft.incomeorloss} onChange={(event) => setField("incomeorloss", moneyTypingValue(event.target.value, 2, true))} /></div>
                                <FieldError message={fieldErrors.incomeorloss} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row><Col className="mt-2 w-100 align-middle text-wrap sectionquestion">*{parseInt(draft.percentownership || "0", 10) >= 25 ? "How do you file your taxes?" : "How do you document your income?"}</Col></Row>
                    <Row className="mt-2 ml-0" data-field="selfincomedoc">
                        {(parseInt(draft.percentownership || "0", 10) >= 25
                            ? [["schedulec", "Schedule C"], ["schedulef", "Schedule F"], ["f1065", "1065 Partnership Return"], ["f1120S", "1120S for S Corp"], ["f1120", "1120 for C Corp"]]
                            : [["w2paystub", "W2/Paystubs"], ["schedulek1", "Schedule K1"]]
                        ).map(([value, label]) => (
                            <Col className="mr-2 p-0" xs="auto" key={value}>
                                <input type="radio" checked={draft.selfincomedoc === value} onChange={(event) => setField("selfincomedoc", event.target.value)} value={value} name="taxes" id={`taxes-${value}`} />
                                <label className="zeitro-radio divlink" htmlFor={`taxes-${value}`}>{label}</label>
                            </Col>
                        ))}
                    </Row>
                    <FieldError message={fieldErrors.selfincomedoc} />
                </>
            )}
            <Row>
                <Col className="px-1 mt-4">
                    <Checkbox className="income-checkbox" onChange={(event) => setField("employedbyagent", event.target.checked)} checked={draft.employedbyagent}>
                        Check if employed by a family member, property seller, real estate agent, or other party to the transaction
                    </Checkbox>
                </Col>
            </Row>
        </>
    )
}

const PreviousEmploymentFields = ({ draft, setDraft, fieldErrors = {}, clearFieldError }) => {
    const setField = (field, value) => {
        setDraft((prev) => ({ ...prev, [field]: value == null ? "" : value }))
        if (clearFieldError && (field === "employername" || field === "from" || field === "to")) {
            clearFieldError(field)
        }
    }
    const setAddressField = (field, value) => setDraft((prev) => {
        const next = { ...prev, [field]: value == null ? "" : `${value}` }
        return { ...next, employeraddress: formatEmployerAddress(next) }
    })
    return (
        <>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12}>
                    <Form.Group className="inputSection">
                        <Form.Label>*Are you the business owner or self-employed?</Form.Label>
                        <CheckGroup
                            className="previous-employment-checkGroup"
                            data={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                            onChangeSelect={(value) => setField("selfemployed", value === "yes")}
                            value={draft.selfemployed ? "yes" : "no"}
                            type="radio"
                            direction="horizontal"
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12} data-field="employername">
                    <Form.Group className="text-left input-groups">
                        <Form.Label className="text-left text-nowrap employment-label">*Employer</Form.Label>
                        <Form.Control
                            placeholder="Enter your employer"
                            type="text"
                            value={draft.employername}
                            isInvalid={!!fieldErrors.employername}
                            onChange={(event) => setField("employername", event.target.value)}
                        />
                        <FieldError message={fieldErrors.employername} />
                    </Form.Group>
                </Col>
                <Col className="ml-0 pl-0 mt-4 employment-autocomplete" md={6} xs={12} data-field="positiontitle">
                    <Autocomplete
                        id="previousTitle"
                        suggestions={titles}
                        name="position"
                        type="text"
                        label="*Position/Title"
                        value={draft.positiontitle}
                        onChange={(event) => {
                            setField("positiontitle", event.target.value)
                            if (clearFieldError) clearFieldError("positiontitle")
                        }}
                    />
                    <FieldError message={fieldErrors.positiontitle} />
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12}>
                    <Form.Group className="text-left">
                        {/* Optional here: a previous employer may have closed or moved on. */}
                        <Form.Label className="text-left text-nowrap">
                            Phone number
                            <Tips content={<div>Specify the HR/Personnel or hiring manager phone number to verify employment.</div>} />
                        </Form.Label>
                        <Form.Control type="text" placeholder={com.phonePlaceholder} pattern={com.phoneRegex} value={draft.phone} onChange={(event) => setField("phone", com.fixPhoneInput(event.target.value))} />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={12} xs={12}>
                    <AddressComponent
                        id="PreviousEmployerAddr"
                        label="Employment address"
                        address={draft.employerstreet}
                        unit={draft.employerunit}
                        state={draft.employerstate}
                        county={draft.employercounty}
                        city={draft.employercity}
                        zip={draft.employerzip}
                        onAddressLineChange={(value) => setAddressField("employerstreet", value)}
                        onUnitChange={(value) => setAddressField("employerunit", value)}
                        onStateChange={(value) => setAddressField("employerstate", value)}
                        onCountyChange={(value) => setAddressField("employercounty", value)}
                        onCityChange={(value) => setAddressField("employercity", value)}
                        onZipChange={(value) => setAddressField("employerzip", value)}
                        validateZip
                        placeholder="Enter employer address"
                    />
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12} data-field="from">
                    <Form.Group className="text-left">
                        <Form.Label className="text-left text-nowrap">*Start date</Form.Label>
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="MM/DD/YYYY"
                            format="MM/DD/YYYY"
                            status={fieldErrors.from ? "error" : undefined}
                            value={draft.from ? dayjs(draft.from, "MM/DD/YYYY") : null}
                            onChange={(date) => setField("from", date ? date.format("MM/DD/YYYY") : "")}
                        />
                        <FieldError message={fieldErrors.from} />
                    </Form.Group>
                </Col>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12} data-field="to">
                    <Form.Group className="text-left">
                        <Form.Label className="text-left text-nowrap">*End date</Form.Label>
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="MM/DD/YYYY"
                            format="MM/DD/YYYY"
                            status={fieldErrors.to ? "error" : undefined}
                            value={draft.to ? dayjs(draft.to, "MM/DD/YYYY") : null}
                            onChange={(date) => setField("to", date ? date.format("MM/DD/YYYY") : "")}
                        />
                        <FieldError message={fieldErrors.to} />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className="ml-0 pl-0 mt-4" md={6} xs={12}>
                    <Form.Group className="text-left">
                        <Form.Label className="text-left text-nowrap">*Total income</Form.Label>
                        <Space.Compact style={{ width: "96%" }}>
                            <DollarInputV2 className="income-dollar-input w-100" value={draft.income} onChange={(value) => setField("income", value == null ? "" : `${value}`)} />
                            <Select style={{ height: 44 }} value={draft.incomeperiod} onChange={(value) => setField("incomeperiod", value)}>
                                <Select.Option value="yearly">Yearly</Select.Option>
                                <Select.Option value="monthly">Monthly</Select.Option>
                            </Select>
                        </Space.Compact>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col className="px-1 mt-4">
                    <Checkbox className="income-checkbox" onChange={(event) => setField("employedbyagent", event.target.checked)} checked={draft.employedbyagent}>
                        Check if employed by a family member, property seller, real estate agent, or other party to the transaction
                    </Checkbox>
                </Col>
            </Row>
        </>
    )
}

const emptyNonQm = {
    nonqmincometype: [],
    nonqmincomeperiod: "monthly",
    nonqmincomeamount: "",
}

const NON_QM_DOC_OPTIONS = [
    { value: "VOE", label: "VOE" },
    { value: "DebtServiceCoverageRatio", label: "Debt Service Coverage Ratio (DSCR)" },
    { value: "PersonalBankStatement12Mo", label: "1 year personal bank statement" },
    { value: "PersonalBankStatement24Mo", label: "2 years personal bank statement" },
    { value: "BusinessBankStatement12Mo", label: "1 year business bank statement" },
    { value: "BusinessBankStatement24Mo", label: "2 years business bank statement" },
    { value: "Form109912Mo", label: "1 year Form 1099" },
    { value: "Form109924Mo", label: "2 years Form 1099" },
    { value: "PL12", label: "1 year profit and loss" },
    { value: "PL24", label: "2 years profit and loss" },
    { value: "AssetDepletion", label: "Asset depletion" },
]

const normalizeNonQm = (nonqm = {}) => ({
    ...emptyNonQm,
    ...nonqm,
    nonqmincometype: Array.isArray(nonqm.nonqmincometype) ? nonqm.nonqmincometype : [],
    nonqmincomeperiod: nonqm.nonqmincomeperiod || "monthly",
    nonqmincomeamount: nonqm.nonqmincomeamount || "",
})

const EmploymentV2 = ({ application, participant, saving, onSave, onPersist, onBack, onContactLoanOfficer }) => {
    const [occupation, setOccupation] = useState(emptyOccupation)
    const [otherOccupations, setOtherOccupations] = useState([])
    const [previousOccupations, setPreviousOccupations] = useState([])
    const [nonqm, setNonqm] = useState(emptyNonQm)
    const [draft, setDraft] = useState(emptyOccupation)
    const [previousDraft, setPreviousDraft] = useState(emptyPreviousOccupation)
    const [showEmploymentModal, setShowEmploymentModal] = useState(false)
    const [showPreviousEmploymentModal, setShowPreviousEmploymentModal] = useState(false)
    const [validated, setValidated] = useState(false)
    const [previousValidated, setPreviousValidated] = useState(false)
    const [editingIndex, setEditingIndex] = useState(-1)
    const [editingPreviousIndex, setEditingPreviousIndex] = useState(-1)
    const [employed2years, setEmployed2years] = useState("false")
    const [skipFormerEmpOnce, setSkipFormerEmpOnce] = useState(false)
    const [showNotice, setShowNotice] = useState(false)
    const [fieldErrors, setFieldErrors] = useState({})
    const [modalFieldErrors, setModalFieldErrors] = useState({})
    const [previousModalFieldErrors, setPreviousModalFieldErrors] = useState({})

    const isNonQmLoan = application?.product?.mortgageapplied === "nonqm"

    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const clearModalFieldError = (field) => {
        setModalFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const clearPreviousModalFieldError = (field) => {
        setPreviousModalFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    useEffect(() => {
        const nextOccupation = normalizeOccupation(participant?.occupation)
        const nextOther = (participant?.otheroccupations || []).map(normalizeOccupation)
        const nextPrevious = (participant?.previousoccupations || []).map(normalizePreviousOccupation)
        setOccupation(nextOccupation)
        setOtherOccupations(nextOther)
        setPreviousOccupations(nextPrevious)
        setNonqm(normalizeNonQm(participant?.nonqm))
        setDraft(nextOccupation)
        setPreviousDraft(emptyPreviousOccupation)
        setEmployed2years(nextPrevious.length > 0 ? "true" : "false")
        setSkipFormerEmpOnce(false)
        setShowNotice(false)
        setFieldErrors({})
        setModalFieldErrors({})
        setPreviousModalFieldErrors({})
    }, [participant])

    const employmentStatus = useMemo(() => {
        if (occupation.hasoccupation === "employed") return "employed"
        if (occupation.hasoccupation === "unemployed" || occupation.hasoccupation === "retired") return "unemployed"
        return ""
    }, [occupation.hasoccupation])

    const employments = occupation.hasoccupation === "employed" ? [occupation, ...otherOccupations] : []
    const totalMonthlyIncome = employments.reduce((sum, item) => sum + calculateEmploymentMonthlyIncome(item), 0)

    const openEmploymentModal = (index = 0) => {
        const selected = index === 0 ? occupation : otherOccupations[index - 1]
        setEditingIndex(index)
        setDraft(normalizeOccupation({ ...selected, hasoccupation: "employed" }))
        setValidated(false)
        setModalFieldErrors({})
        setShowEmploymentModal(true)
    }

    const addEmployment = () => {
        setEditingIndex(-1)
        setDraft(normalizeOccupation({ ...emptyOccupation, key: ensureKey({}, "employment", otherOccupations.length), hasoccupation: "employed" }))
        setValidated(false)
        setModalFieldErrors({})
        setShowEmploymentModal(true)
    }

    const changeEmploymentStatus = (value) => {
        setShowNotice(false)
        clearFieldError("hasoccupation")
        if (value === "employed") {
            setOccupation((prev) => ({ ...prev, hasoccupation: "employed" }))
            if (!occupation.employername || !occupation.positiontitle || !occupation.phone || !occupation.from) {
                openEmploymentModal(0)
            }
            return
        }
        setOccupation((prev) => ({ ...prev, hasoccupation: "unemployed", selfemployed: false }))
        setOtherOccupations([])
    }

    const saveEmploymentModal = async (event) => {
        event.preventDefault()
        event.stopPropagation()
        // Custom English field errors only — do not call reportValidity (browser locale may be Chinese).
        const { errors, firstErrorKey } = validateEmploymentDraft(draft)
        if (firstErrorKey) {
            setModalFieldErrors(errors)
            setValidated(true)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setModalFieldErrors({})
        const next = normalizeOccupation({ ...draft, key: ensureKey(draft, "employment", editingIndex), hasoccupation: "employed" })
        let nextOccupation = occupation
        let nextOtherOccupations = otherOccupations
        if (editingIndex === 0) {
            nextOccupation = next
        } else if (editingIndex > 0) {
            nextOtherOccupations = otherOccupations.map((item, index) => (index === editingIndex - 1 ? next : item))
        } else {
            nextOtherOccupations = [...otherOccupations, next]
        }
        if (onPersist) {
            const persisted = await onPersist(stringifyEmploymentPayload({
                occupation: nextOccupation,
                otheroccupations: nextOtherOccupations,
                previousoccupations: previousOccupations,
                nonqm: isNonQmLoan ? nonqm : undefined,
            }))
            if (persisted === false) {
                return
            }
        }
        setOccupation(nextOccupation)
        setOtherOccupations(nextOtherOccupations)
        setShowEmploymentModal(false)
    }

    const closeEmploymentModal = () => {
        setShowEmploymentModal(false)
        setModalFieldErrors({})
        if (editingIndex === 0 && !occupation.employername && occupation.hasoccupation === "employed") {
            setOccupation((prev) => ({ ...prev, hasoccupation: "" }))
        }
    }

    const setPrimaryEmployment = (index) => {
        if (index === 0) return
        const nextPrimary = otherOccupations[index - 1]
        const oldPrimary = occupation
        setOccupation(nextPrimary)
        setOtherOccupations((prev) => prev.map((item, itemIndex) => (itemIndex === index - 1 ? oldPrimary : item)))
    }

    const deleteEmployment = (index) => {
        if (index === 0) return
        setOtherOccupations((prev) => prev.filter((_, itemIndex) => itemIndex !== index - 1))
    }

    const openPreviousEmploymentModal = (index = -1) => {
        setEditingPreviousIndex(index)
        setPreviousDraft(normalizePreviousOccupation(index >= 0 ? previousOccupations[index] : { ...emptyPreviousOccupation, key: ensureKey({}, "previous-employment", previousOccupations.length) }))
        setPreviousValidated(false)
        setPreviousModalFieldErrors({})
        setShowPreviousEmploymentModal(true)
    }

    const savePreviousEmploymentModal = async (event) => {
        event.preventDefault()
        event.stopPropagation()
        // Custom English field errors only — do not call reportValidity (browser locale may be Chinese).
        const { errors, firstErrorKey } = validatePreviousEmploymentDraft(previousDraft)
        if (firstErrorKey) {
            setPreviousModalFieldErrors(errors)
            setPreviousValidated(true)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setPreviousModalFieldErrors({})
        const next = normalizePreviousOccupation({ ...previousDraft, key: ensureKey(previousDraft, "previous-employment", editingPreviousIndex) })
        const nextPreviousOccupations = editingPreviousIndex >= 0
            ? previousOccupations.map((item, index) => (index === editingPreviousIndex ? next : item))
            : [...previousOccupations, next]
        if (onPersist) {
            const persisted = await onPersist(stringifyEmploymentPayload({
                occupation,
                otheroccupations: otherOccupations,
                previousoccupations: nextPreviousOccupations,
                nonqm: isNonQmLoan ? nonqm : undefined,
            }))
            if (persisted === false) {
                return
            }
        }
        if (editingPreviousIndex >= 0) {
            setPreviousOccupations(nextPreviousOccupations)
        } else {
            setPreviousOccupations(nextPreviousOccupations)
        }
        setEmployed2years("true")
        setShowPreviousEmploymentModal(false)
    }

    const deletePreviousEmployment = (index) => {
        setPreviousOccupations((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
    }

    const changePreviousJob = (value) => {
        setEmployed2years(value)
        if (value === "true" && previousOccupations.length === 0) {
            openPreviousEmploymentModal(-1)
        }
        if (value === "false") {
            setPreviousOccupations([])
        }
    }

    const requiresFormerEmployment = () => {
        if (skipFormerEmpOnce || previousOccupations.length > 0) return false
        if (occupation.hasoccupation === "unemployed" && employed2years === "false") return true
        if (occupation.hasoccupation === "employed" && occupation.from) {
            return dayjs().diff(dayjs(occupation.from, "MM/DD/YYYY"), "month") < 24
        }
        return false
    }

    const handleNext = () => {
        if (isNonQmLoan) {
            if (!nonqm.nonqmincomeamount) {
                setFieldErrors({ nonqmincomeamount: "Please provide your non-QM income amount." })
                setTimeout(() => scrollToField("nonqmincomeamount"), 50)
                return
            }
            setFieldErrors({})
            onSave(stringifyEmploymentPayload({
                occupation,
                otheroccupations: otherOccupations,
                previousoccupations: previousOccupations,
                nonqm,
            }))
            return
        }
        if (!occupation.hasoccupation) {
            setFieldErrors({ hasoccupation: "Please select your occupation." })
            setTimeout(() => scrollToField("hasoccupation"), 50)
            return
        }
        setFieldErrors({})
        if (requiresFormerEmployment()) {
            setShowNotice(true)
            return
        }
        onSave(stringifyEmploymentPayload({
            occupation,
            otheroccupations: otherOccupations,
            previousoccupations: previousOccupations,
        }))
    }

    const employmentModal = () => (
        <Modal className="employment-modal" size="lg" show={showEmploymentModal} onHide={closeEmploymentModal} backdrop="static">
            <Modal.Header closeButton><Modal.Title>{editingIndex >= 0 ? "Edit employment" : "Add employment"}</Modal.Title></Modal.Header>
            <Form noValidate onSubmit={saveEmploymentModal} validated={validated}>
                <Modal.Body style={{ padding: "0 40px 14px", maxHeight: "80vh", overflow: "auto" }}>
                    <div style={{ fontSize: "14px", lineHeight: "20px", color: "#6E6E70", margin: "14px 0 16px 0" }}>* Indicates required</div>
                    <Row>
                        <Col className="mt-2 pl-0" md={6} xs={12}>
                            <Form.Group className="inputSection">
                                <Form.Label>*Are you the business owner or self-employed?</Form.Label>
                                <CheckGroup
                                    data={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                                    onChangeSelect={(value) => {
                                        setDraft((prev) => ({ ...prev, selfemployed: value === "yes" }))
                                        setModalFieldErrors({})
                                    }}
                                    value={draft.selfemployed ? "yes" : "no"}
                                    type="radio"
                                    direction="horizontal"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <EmploymentFields
                        draft={draft}
                        setDraft={setDraft}
                        fieldErrors={modalFieldErrors}
                        clearFieldError={clearModalFieldError}
                    />
                </Modal.Body>
                <Modal.Footer><BootstrapButton type="submit" variant="zeitro-primary" className="income-save-btn" disabled={saving}>Save</BootstrapButton></Modal.Footer>
            </Form>
        </Modal>
    )

    const previousEmploymentModal = () => (
        <Modal className="employment-modal" size="lg" show={showPreviousEmploymentModal} onHide={() => { setShowPreviousEmploymentModal(false); setPreviousModalFieldErrors({}) }} backdrop="static">
            <Modal.Header closeButton><Modal.Title>{editingPreviousIndex >= 0 ? "Edit previous employment" : "Add previous employment"}</Modal.Title></Modal.Header>
            <Form noValidate onSubmit={savePreviousEmploymentModal} validated={previousValidated}>
                <Modal.Body style={{ padding: "0 40px 14px", maxHeight: "80vh", overflow: "auto" }}>
                    <div style={{ fontSize: "14px", lineHeight: "20px", color: "#6E6E70", margin: "14px 0 16px 0" }}>* Indicates required</div>
                    <PreviousEmploymentFields
                        draft={previousDraft}
                        setDraft={setPreviousDraft}
                        fieldErrors={previousModalFieldErrors}
                        clearFieldError={clearPreviousModalFieldError}
                    />
                </Modal.Body>
                <Modal.Footer><BootstrapButton type="submit" variant="zeitro-primary" className="income-save-btn" disabled={saving}>Save</BootstrapButton></Modal.Footer>
            </Form>
        </Modal>
    )

    return (
        <>
            <div className="text-left mb-3 application-v2-form-body income-application newInterviewPaneProperty">
                <div className="page-header page-header-row">
                    <span className="page-header-primary">Personal</span>
                    <span className="page-header-separator">/</span>
                    <span className="page-header-secondary">Income</span>
                </div>
                {employmentModal()}
                {previousEmploymentModal()}
                {isNonQmLoan ? (
                    <div>
                        <div className="mt-4">
                            <Form.Group className="inputSection">
                                <Form.Label>Which income documentation would you like to submit for loan qualification?</Form.Label>
                                <Checkbox.Group
                                    className="w-600px d-flex flex-column gap-3"
                                    value={nonqm.nonqmincometype}
                                    onChange={(value) => setNonqm((prev) => ({ ...prev, nonqmincometype: value }))}
                                >
                                    {NON_QM_DOC_OPTIONS.map((option) => (
                                        <Checkbox key={option.value} value={option.value}>{option.label}</Checkbox>
                                    ))}
                                </Checkbox.Group>
                            </Form.Group>
                        </div>
                        <div className="mt-4">
                            <Form.Group className="text-left" style={{ alignItems: "baseline", width: "100%", justifyContent: "space-between" }} data-field="nonqmincomeamount">
                                <Form.Label className="text-left text-nowrap employment-label">*What is your estimated income?</Form.Label>
                                <div className="w-600px">
                                    <Space.Compact className="w-100">
                                        <DollarInputV2
                                            value={nonqm.nonqmincomeamount}
                                            onChange={(value) => {
                                                setNonqm((prev) => ({ ...prev, nonqmincomeamount: `${value || ""}` }))
                                                clearFieldError("nonqmincomeamount")
                                            }}
                                        />
                                        <Select
                                            style={{ height: 44, width: "40%" }}
                                            value={nonqm.nonqmincomeperiod}
                                            onChange={(value) => setNonqm((prev) => ({ ...prev, nonqmincomeperiod: value }))}
                                        >
                                            <Select.Option value="monthly">Monthly</Select.Option>
                                            <Select.Option value="yearly">Yearly</Select.Option>
                                        </Select>
                                    </Space.Compact>
                                    <FieldError message={fieldErrors.nonqmincomeamount} />
                                </div>
                            </Form.Group>
                        </div>
                    </div>
                ) : (
                    <>
                {showNotice && createPortal(
                    <div className="notice-card">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "13px" }}>
                            <div
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    borderRadius: "50%",
                                    backgroundColor: "#FFFFFF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <InfoCircleFilled style={{ color: "#13358D", fontSize: "18px" }} />
                            </div>
                            <span style={{ fontSize: "14px", fontWeight: 600, color: "#13358D", lineHeight: "16.94px" }}>
                                Notice
                            </span>
                        </div>
                        <div
                            style={{
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: "18px",
                                color: "#333333",
                                marginBottom: "13px",
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                whiteSpace: "normal",
                            }}
                        >
                            To complete your loan application, we strongly encourage you to provide at least 2 years of employment history, as it is typically required for processing. However, if you're unable to provide this information at the moment, you may skip it for now and still proceed with your application.
                        </div>
                        <div className="notice-card-btn">
                            {onContactLoanOfficer && (
                                <BootstrapButton variant="link" className="notice-link-btn" onClick={() => { setShowNotice(false); onContactLoanOfficer() }}>
                                    Contact loan officer
                                </BootstrapButton>
                            )}
                            <BootstrapButton
                                variant="light"
                                className="notice-skip-btn"
                                onClick={() => { setShowNotice(false); setSkipFormerEmpOnce(true) }}
                            >
                                Skip
                            </BootstrapButton>
                            <BootstrapButton
                                variant="primary"
                                className="notice-primary-btn"
                                onClick={() => { setShowNotice(false); setEmployed2years("true"); openPreviousEmploymentModal(-1) }}
                            >
                                Add former employment
                            </BootstrapButton>
                        </div>
                    </div>,
                    document.body,
                )}
                <Row>
                    <Col className="mt-4 pl-0" md={6} xs={12}>
                        <Form.Group className="inputSection" data-field="hasoccupation">
                            <Form.Label>*What is your current employment status?</Form.Label>
                            <CheckGroup data={[{ value: "employed", label: "Employed" }, { value: "unemployed", label: "Unemployed/Retired" }]} onChangeSelect={changeEmploymentStatus} value={employmentStatus} type="radio" direction="horizontal" required />
                            <FieldError message={fieldErrors.hasoccupation} />
                        </Form.Group>
                    </Col>
                </Row>
                {occupation.hasoccupation === "employed" && employments.length > 0 && (
                    <Row>
                        <Col className="mt-4 pl-0" md={12} xs={12}>
                            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8, color: "#62707C" }}>Employments</div>
                            {employments.map((employment, index) => (
                                <div className="employment-list mt-2" key={employment.key || index}>
                                    <div className="employment-item">
                                        <div className="employment-top">
                                            <div className="employment-top-l">
                                                <div className="employment-icon"><img src="/images/employmentIcon.png" alt="employment" /></div>
                                                <span className="employment-name">Employment {index + 1}</span>
                                            </div>
                                            <div className="employment-top-r">
                                                {index === 0 && <div className="primary-employment-tag">Primary employment</div>}
                                                {index === 0 && <div className="primary-employment-tag-mobile">Primary employment</div>}
                                            </div>
                                        </div>
                                        <Row className="employment-form mb-2">
                                            {incomeRows(employment).map((item, formIndex) => item.show && (
                                                <Col className="employment-form-item" md={item.span || 6} key={formIndex}>
                                                    <div className="employment-form-label">{item.label}<span className="employment-form-label-colon">:</span></div>
                                                    <div className="employment-form-value">{item.value}<span style={{ marginLeft: 10 }}>{item.monthly === "yearly" ? "/year" : item.monthly === "monthly" ? "/month" : ""}</span></div>
                                                </Col>
                                            ))}
                                        </Row>
                                        <div className="employment-bottom">
                                            <div className="employment-bottom-left">
                                                <AntButton className="employment-edit-btn" onClick={() => openEmploymentModal(index)}>Edit</AntButton>
                                                {index !== 0 && <AntButton className="employment-remove-btn" type="text" onClick={() => deleteEmployment(index)}>Remove</AntButton>}
                                            </div>
                                            <div><AntButton className="employment-primary-btn" disabled={index === 0} type="text" onClick={() => setPrimaryEmployment(index)}>Set as primary</AntButton></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="add-job" style={{ marginTop: "22px" }} onClick={addEmployment}><span>Add employment</span></div>
                            <div style={{ height: "1px", backgroundColor: "#DBDCDE", margin: "16px 0" }} />
                            <div style={{ fontSize: 16, fontWeight: 500, color: "#222222", textAlign: "right" }}>Total monthly income: {formatAmount(totalMonthlyIncome)}</div>
                        </Col>
                    </Row>
                )}
                {occupation.hasoccupation === "unemployed" && (
                    <>
                        <Row>
                            <Col className="mt-2 px-0" md={12} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>* If you claim employment income, you will need to demonstrate at least two years of employment, which can include tertiary education.</Form.Label>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="mt-2 pl-0" md={6} xs={12}>
                                <Form.Group className="inputSection">
                                    <Form.Label>Have you been employed in the previous two years?</Form.Label>
                                    <CheckGroup data={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]} onChangeSelect={changePreviousJob} value={employed2years} type="radio" direction="horizontal" required />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}
                {previousOccupations.length > 0 && (
                    <Row>
                        <Col className="mt-4 pl-0" md={12} xs={12}>
                            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8, color: "#62707C" }}>Previous employments</div>
                            {previousOccupations.map((employment, index) => (
                                <div className="employment-list" key={employment.key || index}>
                                    <div className="employment-item">
                                        <div className="employment-top">
                                            <div className="employment-top-l">
                                                <div className="employment-icon"><img src="/images/employmentIcon.png" alt="employment" /></div>
                                                <span className="employment-name">Employment {index + 1}</span>
                                            </div>
                                        </div>
                                        <Row className="employment-form">
                                            {previousRows(employment).map((item, formIndex) => (
                                                <Col className="employment-form-item" md={item.span || 6} key={formIndex}>
                                                    <div className="employment-form-label">{item.label} <span className="employment-form-label-colon">:</span></div>
                                                    <div className="employment-form-value">{item.value}</div>
                                                </Col>
                                            ))}
                                        </Row>
                                        <div className="employment-bottom">
                                            <div className="employment-bottom-left">
                                                <AntButton className="employment-edit-btn" onClick={() => openPreviousEmploymentModal(index)}>Edit</AntButton>
                                                <AntButton className="employment-remove-btn" type="text" onClick={() => deletePreviousEmployment(index)}>Remove</AntButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="add-job" style={{ marginTop: "12px" }} onClick={() => openPreviousEmploymentModal(-1)}><span>Add employment</span></div>
                        </Col>
                    </Row>
                )}
                    </>
                )}
            </div>
            <Row className="text-left application-bottom-bar">
                <div className="d-flex align-items-center">
                    <BootstrapButton id="previous" type="button" variant="zeitro-primary" disabled={saving} className="operate-btn back mr-3" onClick={onBack}>Back</BootstrapButton>
                    <BootstrapButton id="next" type="button" variant="zeitro-primary" disabled={saving} className="text-center operate-btn btn" onClick={handleNext}>Next</BootstrapButton>
                </div>
            </Row>
        </>
    )
}

export default EmploymentV2
